// Cómo se le presenta un archivo a Pau (04-11-2026; web/qa/auditoria-apple/propuesta-2/fuentes/pdf.md §3): por lo que ES
// —«La oficina, limpia, ordenada y bonita — plan» · «PDF · 11 páginas · 2,2 MB»— y no por su nombre de archivo. El
// mismo vocabulario en la tarjeta del chat, la rejilla «Fotos y archivos» y la barra del visor. Puro.
import { t } from '../../../i18n';
import type { StoredAttachment } from '../../../types';
import type { ArchivoDTO } from '../compartido/vocabulario';
import { esInforme, portada } from '../informe/bloques';

/** Lo que hace falta para presentar un archivo, venga del chat (StoredAttachment) o de la galería (ArchivoDTO). */
export interface DatosArchivo {
  id: string;
  /** Nombre de archivo (con él se descarga). */
  nombre: string;
  tipo: string;
  tamano: number;
  /** Título legible que puso el servidor (metadatos del PDF, el que dio el agente, o la línea más grande de la portada). */
  titulo?: string;
  paginas?: number;
  /** Ancho/alto de la página 1 de un PDF. */
  proporcion?: number;
  /** El principio de un informe (su «# título» y su entradilla). */
  resumen?: string;
}

export const desdeAdjunto = (a: StoredAttachment): DatosArchivo => ({
  id: a.id,
  nombre: a.displayName || a.name,
  tipo: a.mimeType,
  tamano: a.size,
  ...(a.title ? { titulo: a.title } : {}),
  ...(a.pageCount ? { paginas: a.pageCount } : {}),
  ...(a.pageAspect ? { proporcion: a.pageAspect } : {}),
  ...(a.textPreview ? { resumen: a.textPreview } : {}),
});

export const desdeArchivoDTO = (a: ArchivoDTO): DatosArchivo => ({
  id: a.id,
  nombre: a.nombre,
  tipo: a.imagen && !a.tipo.startsWith('image/') ? 'image/png' : a.tipo,
  tamano: a.tamano,
  ...(a.titulo ? { titulo: a.titulo } : {}),
  ...(a.paginas ? { paginas: a.paginas } : {}),
});

export type Clase = 'pdf' | 'imagen' | 'informe' | 'texto' | 'otro';

export function claseDe(d: Pick<DatosArchivo, 'tipo' | 'nombre'>): Clase {
  if (d.tipo === 'application/pdf') return 'pdf';
  if (d.tipo.startsWith('image/')) return 'imagen';
  if (esInforme({ tipo: d.tipo, nombre: d.nombre })) return 'informe';
  if (d.tipo.startsWith('text/') || d.tipo === 'application/json') return 'texto';
  return 'otro';
}

/** «plan-oficina.pdf» → «Plan oficina»; «IMG_2041.png» → «IMG 2041». El último recurso del título. */
export function humanizarNombre(nombre: string): string {
  const sinExt = nombre.replace(/\.[a-z0-9]{1,5}$/i, '');
  const limpio = sinExt.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim() || nombre;
  return limpio.charAt(0).toUpperCase() + limpio.slice(1);
}

/** El título que se lee: el del servidor → el «# …» de un informe → el nombre de archivo en lenguaje normal. */
export function tituloDe(d: DatosArchivo): string {
  if (claseDe(d) === 'informe') {
    const delInforme = portada(d.resumen ?? '').titulo;
    if (delInforme) return delInforme;
  }
  return d.titulo?.trim() || humanizarNombre(d.nombre);
}

// La oficina habla español con coma decimal («2,2 MB»), como la Sala de Control.
const DECIMAL = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1, minimumFractionDigits: 1 });

export function tamanoLegible(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${DECIMAL.format(bytes / 1024 / 1024)} MB`;
}

/** «PDF», «Informe», «TXT»…: la etiqueta corta del tipo. */
export function etiquetaDe(d: Pick<DatosArchivo, 'tipo' | 'nombre'>): string {
  const clase = claseDe(d);
  if (clase === 'pdf') return 'PDF';
  if (clase === 'informe') return t('oficinaV2.archivo.informe');
  const ext = /\.([a-z0-9]{1,5})$/i.exec(d.nombre)?.[1];
  return ext ? ext.toUpperCase() : t('oficinaV2.archivo.documento');
}

export function paginasLegibles(n: number): string {
  return n === 1 ? t('oficinaV2.archivo.unaPagina') : t('oficinaV2.archivo.paginas', { n });
}

/** «PDF · 11 páginas · 2,2 MB» (la línea gris de la tarjeta y de la barra del visor). */
export function detalleDe(d: DatosArchivo): string {
  return [etiquetaDe(d), d.paginas ? paginasLegibles(d.paginas) : null, tamanoLegible(d.tamano) || null].filter(Boolean).join(' · ');
}
